import {
  useId,
  useState,
  useCallback,
  useEffect,
  type ComponentPropsWithRef,
  type ChangeEvent,
  type UIEvent,
  forwardRef,
} from "react";
import { useObjectRef } from "react-aria";
import {
  CoreTextArea,
  type PropsWithOptionalTestID,
} from "@boi/components-core";

import * as S from "./TextArea.styled";
import { InputLabelTop, type InputLabelTopProps } from "../InputLabelTop";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { OuterField } from "../OuterField";
import { CharactersCounter } from "../CharactersCounter";
import { buildAlertAssociationProps } from "../Alert/AlertAssociationProps";

export type TextAreaProps = PropsWithOptionalTestID &
  Omit<
    ComponentPropsWithRef<"textarea">,
    "value" | "defaultValue" | "style" | "className" | "aria-invalid"
  > & {
    value?: string;
    defaultValue?: string;
    hasError?: boolean;
    autoGrow?: boolean;
    autoGrowMaxLines?: number;
    noCounter?: boolean;
    alertProps?:
      | InputAlertsProps["alerts"][number]
      | InputAlertsProps["alerts"];
    labelProps: Omit<
      InputLabelTopProps,
      "htmlFor" | "labelHintID" | "children"
    >;
  };

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (props, forwardedRef) => {
    const {
      id,
      hasError,
      autoGrow,
      autoGrowMaxLines,
      onChange,
      onScroll,
      testID = "textarea",
      labelProps,
      alertProps,
      noCounter,
      ...inputProps
    } = props;

    const textAreaRef = useObjectRef(forwardedRef);
    const fallbackID = useId();
    const labelHintID = useId();
    const alertId = useId();

    const { labelHint } = labelProps;
    const { maxLength } = inputProps;

    const showCounter = !noCounter && maxLength;

    const safeLabelHintID = labelHint ? labelHintID : undefined;
    const ariaAlertAssociationProps = buildAlertAssociationProps(
      alertId,
      props,
      safeLabelHintID
    );

    const [length, setLength] = useState(
      (inputProps.value || inputProps.defaultValue)?.length || 0
    );
    const [displayInnerShadow, setDisplayInnerShadow] =
      useState<CoreTextArea.DisplayInnerShadow>(null);

    const calculateScrollPercentage = () => {
      const element = textAreaRef.current;
      if (element) {
        const { scrollTop, scrollHeight, offsetHeight } = element;
        setDisplayInnerShadow(
          CoreTextArea.determineInnerShadowToDisplay(
            scrollTop,
            scrollHeight,
            offsetHeight
          )
        );
      }
    };

    const handleScroll = useCallback(
      (evt: UIEvent<HTMLTextAreaElement>) => {
        calculateScrollPercentage();
        onScroll?.(evt);
      },
      [onScroll]
    );

    const handleChange = useCallback(
      (evt: ChangeEvent<HTMLTextAreaElement>) => {
        setLength(evt.target.value.length);
        onChange?.(evt);
      },
      [onChange]
    );

    useEffect(() => {
      if (textAreaRef.current) {
        const observer = new ResizeObserver(calculateScrollPercentage);

        observer.observe(textAreaRef.current);
        return () => observer.disconnect();
      }
    }, [textAreaRef.current]);

    return (
      <InputWrapper
        gap={showCounter ? "4px" : "8px"}
        alert={
          alertProps && (
            <div id={alertId} data-testid={`${testID}-alerts`}>
              <InputAlerts
                alerts={Array.isArray(alertProps) ? alertProps : [alertProps]}
              />
            </div>
          )
        }
      >
        <InputLabelTop
          {...labelProps}
          labelHintID={labelHintID}
          htmlFor={id || fallbackID}
        >
          <OuterField hasError={hasError}>
            <S.Wrapper $displayInnerShadow={displayInnerShadow}>
              <S.CustomTextArea
                {...inputProps}
                {...ariaAlertAssociationProps}
                ref={textAreaRef}
                id={id || fallbackID}
                onChange={handleChange}
                onScroll={handleScroll}
                maxRows={autoGrow ? autoGrowMaxLines || Infinity : 4}
                data-testid={testID}
              />
            </S.Wrapper>
          </OuterField>
        </InputLabelTop>

        {showCounter ? (
          <CharactersCounter
            length={length}
            maxLength={maxLength}
            testID={`${testID}-characters-counter`}
          />
        ) : undefined}
      </InputWrapper>
    );
  }
);

TextArea.displayName = "TextArea";

export default TextArea;
